import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { newIdempotencyKey, SubmitError, submitPublic } from '../lib/publicSubmit';
import { getAttribution } from '../lib/attribution';
import { track } from '../lib/analytics';
import { useSchool } from '../contexts/SchoolContext';
import Turnstile from './Turnstile';
import './ApplicationForm.css';

// ---------------------------------------------------------------------------------------------- definitions
export const EMPTY_FORM = {
  studentFirstName: '', studentLastName: '', dateOfBirth: '', gender: '', grade: '', previousSchool: '',
  parentFirstName: '', parentLastName: '', email: '', phone: '', address: '', city: '', state: '', zipCode: '',
  emergencyContactName: '', emergencyContactPhone: '', emergencyContactRelation: '',
  medicalConditions: '', specialNeeds: '', howDidYouHear: '', comments: ''
};

export const STEPS = ['Student', 'Parent / Guardian', 'Additional information', 'Review and submit'];

// Fields checked on each step (the review step re-checks all of them).
const STEP_FIELDS = [
  ['studentFirstName', 'studentLastName', 'dateOfBirth', 'gender', 'grade'],
  ['parentFirstName', 'parentLastName', 'email', 'phone', 'address', 'city', 'state', 'zipCode',
    'emergencyContactName', 'emergencyContactPhone', 'emergencyContactRelation'],
  [],
  []
];

const PHONE = /^[0-9+()\-\s]{7,20}$/;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const MAX_AGE_YEARS = 25;

const isoToday = () => new Date().toISOString().slice(0, 10);

// YYYY-MM-DD that is an actual calendar date (Date.parse alone would roll 31 February over to March).
const isRealDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
};

// Returns { field: message } for the given step. Mirrors the checks the database repeats on submit.
export const validateStep = (step, d) => {
  const e = {};
  const need = (field, message) => {
    if (!String(d[field] ?? '').trim()) e[field] = message;
  };
  if (step === 0) {
    need('studentFirstName', 'Student first name is required');
    need('studentLastName', 'Student last name is required');
    if (!d.dateOfBirth) {
      e.dateOfBirth = 'Date of birth is required';
    } else {
      const today = isoToday();
      const oldest = `${Number(today.slice(0, 4)) - MAX_AGE_YEARS}${today.slice(4)}`;
      if (!isRealDate(d.dateOfBirth)) {
        e.dateOfBirth = 'Enter a valid date of birth';
      } else if (d.dateOfBirth > today) {
        e.dateOfBirth = 'Date of birth cannot be in the future';
      } else if (d.dateOfBirth < oldest) {
        e.dateOfBirth = 'Enter a valid date of birth';
      }
    }
    need('gender', 'Gender is required');
    need('grade', 'Grade/Program is required');
  }
  if (step === 1) {
    need('parentFirstName', 'Parent first name is required');
    need('parentLastName', 'Parent last name is required');
    if (!d.email.trim()) e.email = 'Email is required';
    else if (!EMAIL.test(d.email.trim())) e.email = 'Enter a valid email address';
    if (!d.phone.trim()) e.phone = 'Phone number is required';
    else if (!PHONE.test(d.phone.trim())) e.phone = 'Enter a valid phone number (digits, +, spaces, dashes, brackets)';
    need('address', 'Address is required');
    need('city', 'City is required');
    need('state', 'State is required');
    need('zipCode', 'Zip code is required');
    need('emergencyContactName', 'Emergency contact name is required');
    if (!d.emergencyContactPhone.trim()) e.emergencyContactPhone = 'Emergency contact phone is required';
    else if (!PHONE.test(d.emergencyContactPhone.trim())) e.emergencyContactPhone = 'Enter a valid phone number';
    need('emergencyContactRelation', 'Relationship is required');
  }
  return e;
};

export const validateAll = (d) => {
  const errors = {};
  let firstBadStep = null;
  [0, 1].forEach((s) => {
    const e = validateStep(s, d);
    if (Object.keys(e).length && firstBadStep === null) firstBadStep = s;
    Object.assign(errors, e);
  });
  return { errors, firstBadStep };
};

const toSnakeCase = (key) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

// -------------------------------------------------------------------------------------------- draft storage
// A partly completed application survives a refresh or a dropped connection. The medical and special-needs
// answers are sensitive, so they are never written to the browser: they must be re-entered after a reload.
const DRAFT_KEY = 'ksms_application_draft_v1';
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const NOT_SAVED = ['medicalConditions', 'specialNeeds'];

const loadDraft = () => {
  try {
    const draft = JSON.parse(window.localStorage.getItem(DRAFT_KEY));
    if (!draft || Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) return null;
    return draft;
  } catch {
    return null;
  }
};
const saveDraft = (data, idempotencyKey) => {
  try {
    const safe = Object.fromEntries(Object.entries(data).filter(([k]) => !NOT_SAVED.includes(k)));
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify({ data: safe, idempotencyKey, savedAt: Date.now() }));
  } catch {
    // Storage may be unavailable; the form still works without a draft.
  }
};
const clearDraft = () => {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
};
const hasContent = (data) => Object.entries(data).some(([k, v]) => !NOT_SAVED.includes(k) && String(v).trim() !== '');

// ------------------------------------------------------------------------------------------------ component
const ApplicationForm = () => {
  const { settings, grades, gradesError, eligibleGrades, gradeLabel } = useSchool();

  const restored = useMemo(loadDraft, []);
  const [data, setData] = useState(() => ({ ...EMPTY_FORM, ...(restored?.data ?? {}) }));
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [notice, setNotice] = useState(restored && hasContent(restored.data) ? 'restored' : '');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReset, setTurnstileReset] = useState(0);
  // One key per logical submission, kept with the draft: a retry, a double click or a refresh returns the original.
  const idempotencyKey = useRef(restored?.idempotencyKey ?? newIdempotencyKey());
  const heading = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (reference) return;
    if (hasContent(data)) saveDraft(data, idempotencyKey.current);
  }, [data, reference]);

  const focusHeading = useCallback(() => {
    // Move focus to the step heading so keyboard and screen-reader users land at the top of the new step.
    setTimeout(() => heading.current?.focus(), 0);
  }, []);

  const change = (e) => {
    const { name, value } = e.target;
    if (!started.current) {
      started.current = true;
      track('application_started');
    }
    setData((d) => ({ ...d, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const goTo = (next) => {
    setStatus({ type: '', message: '' });
    setStep(next);
    focusHeading();
    window.scrollTo?.({ top: 0 });
  };

  const next = () => {
    const found = validateStep(step, data);
    setErrors(found);
    if (Object.keys(found).length) {
      setStatus({ type: 'error', message: 'Please fix the highlighted fields to continue' });
      const first = STEP_FIELDS[step].find((f) => found[f]);
      setTimeout(() => document.getElementById(`field-${first}`)?.focus(), 0);
      return;
    }
    goTo(step + 1);
  };

  const startOver = () => {
    clearDraft();
    setData({ ...EMPTY_FORM });
    setErrors({});
    setConsent(false);
    setNotice('');
    idempotencyKey.current = newIdempotencyKey();
    setStep(0);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const { errors: found, firstBadStep } = validateAll(data);
    if (firstBadStep !== null) {
      setErrors(found);
      setStatus({ type: 'error', message: 'Some required information is missing or invalid' });
      setStep(firstBadStep);
      focusHeading();
      return;
    }
    if (!consent) {
      setStatus({ type: 'error', message: 'Please accept the privacy policy to submit your application' });
      return;
    }
    if (!turnstileToken) {
      setStatus({ type: 'error', message: 'Please complete the spam check and try again.' });
      return;
    }

    setSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      const payload = {
        ...Object.fromEntries(Object.entries(data).map(([k, v]) => [toSnakeCase(k), typeof v === 'string' ? v.trim() : v])),
        consent: true,
        ...getAttribution()
      };
      const result = await submitPublic({
        kind: 'admission',
        payload,
        idempotencyKey: idempotencyKey.current,
        turnstileToken
      });
      clearDraft();
      idempotencyKey.current = newIdempotencyKey();
      track('application_submitted', { grade: data.grade });
      setReference(result.reference_number);
      window.scrollTo?.({ top: 0, behavior: 'smooth' });
      focusHeading();
    } catch (error) {
      setStatus({
        type: 'error',
        message: error instanceof SubmitError ? error.message : 'Failed to submit application. Please try again.'
      });
    } finally {
      setSubmitting(false);
      setTurnstileToken('');
      setTurnstileReset((n) => n + 1);
    }
  };

  // -------------------------------------------------------------------------------------------- confirmation
  if (reference) {
    return (
      <div className="application-form-container application-confirmation" role="status">
        <h2 ref={heading} tabIndex={-1}>Thank you, your application has been received</h2>
        <p>Your application reference number is:</p>
        <p className="reference-number" data-testid="reference-number">{reference}</p>
        <p>Please keep this number. Quote it whenever you contact the school about this application.</p>
        <p>
          You can check the progress of your application at any time on{' '}
          <Link to="/track">Track my application</Link> with this reference number and your email address.
        </p>

        <h3>What happens next</h3>
        <ol>
          <li>The admissions team reviews your application.</li>
          <li>The school will contact you using the email address and phone number you gave.</li>
          <li>You may be asked for more information or invited to visit the school.</li>
        </ol>

        <h3>Questions?</h3>
        <p>
          {settings.email_admissions && (
            <>Email <a href={`mailto:${settings.email_admissions}`}>{settings.email_admissions}</a></>
          )}
          {settings.email_admissions && settings.phones.length > 0 && ' or call '}
          {settings.phones.map((phone, index) => (
            <React.Fragment key={phone.tel}>
              {index > 0 && ' | '}
              <a href={`tel:${phone.tel}`}>{phone.display}</a>
            </React.Fragment>
          ))}
          .
        </p>
      </div>
    );
  }

  // ------------------------------------------------------------------------------------------------ fields
  const field = (name, label, { type = 'text', required = true, hint, ...rest } = {}) => (
    <div className="form-group">
      <label htmlFor={`field-${name}`}>
        {label}{required && <span aria-hidden="true"> *</span>}
        {required && <span className="visually-hidden"> (required)</span>}
      </label>
      <input
        id={`field-${name}`}
        type={type}
        name={name}
        value={data[name]}
        onChange={change}
        className={errors[name] ? 'error' : ''}
        aria-required={required || undefined}
        aria-invalid={errors[name] ? 'true' : undefined}
        aria-describedby={errors[name] ? `error-${name}` : hint ? `hint-${name}` : undefined}
        {...rest}
      />
      {hint && !errors[name] && <small id={`hint-${name}`}>{hint}</small>}
      {errors[name] && <span id={`error-${name}`} className="error-message" role="alert">{errors[name]}</span>}
    </div>
  );

  const area = (name, label, props = {}) => (
    <div className="form-group">
      <label htmlFor={`field-${name}`}>{label}</label>
      <textarea id={`field-${name}`} name={name} value={data[name]} onChange={change} rows="3" {...props} />
    </div>
  );

  const select = (name, label, options, { required = true, placeholder } = {}) => (
    <div className="form-group">
      <label htmlFor={`field-${name}`}>
        {label}{required && <span aria-hidden="true"> *</span>}
        {required && <span className="visually-hidden"> (required)</span>}
      </label>
      <select
        id={`field-${name}`}
        name={name}
        value={data[name]}
        onChange={change}
        className={errors[name] ? 'error' : ''}
        aria-required={required || undefined}
        aria-invalid={errors[name] ? 'true' : undefined}
        aria-describedby={errors[name] ? `error-${name}` : undefined}
      >
        <option value="">{placeholder}</option>
        {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
      {errors[name] && <span id={`error-${name}`} className="error-message" role="alert">{errors[name]}</span>}
    </div>
  );

  const review = (title, stepIndex, rows) => (
    <section className="review-section" aria-label={title}>
      <div className="review-header">
        <h4>{title}</h4>
        <button type="button" className="link-button" onClick={() => goTo(stepIndex)}>Edit<span className="visually-hidden"> {title}</span></button>
      </div>
      <dl>
        {rows.filter(([, value]) => String(value ?? '').trim() !== '').map(([label, value]) => (
          <div key={label} className="review-row"><dt>{label}</dt><dd>{value}</dd></div>
        ))}
      </dl>
    </section>
  );

  const gender = { male: 'Male', female: 'Female', other: 'Other' }[data.gender] ?? data.gender;
  const heard = {
    website: 'Website', 'social-media': 'Social Media', friend: 'Friend/Referral',
    advertisement: 'Advertisement', other: 'Other'
  }[data.howDidYouHear] ?? data.howDidYouHear;

  // ------------------------------------------------------------------------------------------------- render
  return (
    <div className="application-form-container">
      <h2>Online Application Form</h2>

      <nav aria-label="Application progress">
        <ol className="step-indicator">
          {STEPS.map((name, i) => (
            <li key={name} className={i === step ? 'current' : i < step ? 'done' : ''} aria-current={i === step ? 'step' : undefined}>
              <span className="step-number">{i < step ? '✓' : i + 1}</span>
              <span className="step-name">{name}</span>
            </li>
          ))}
        </ol>
        <p className="step-count" aria-live="polite">Step {step + 1} of {STEPS.length}</p>
      </nav>

      {notice === 'restored' && (
        <div className="alert alert-info" role="status">
          We restored your saved draft. For privacy, medical and special-needs answers are not saved and need to be
          entered again.{' '}
          <button type="button" className="link-button" onClick={startOver}>Start over</button>
        </div>
      )}

      {status.message && <div className={`alert alert-${status.type}`} role="alert">{status.message}</div>}

      <form onSubmit={submit} className="application-form" noValidate>
        <p className="required-note"><span aria-hidden="true">*</span> Required information</p>

        {step === 0 && (
          <fieldset className="form-section">
            <legend><h3 ref={heading} tabIndex={-1}>Student information</h3></legend>
            <div className="form-row">
              {field('studentFirstName', 'First name', { autoComplete: 'off' })}
              {field('studentLastName', 'Last name', { autoComplete: 'off' })}
            </div>
            <div className="form-row">
              {field('dateOfBirth', 'Date of birth', { type: 'date', max: isoToday() })}
              {select('gender', 'Gender', [['male', 'Male'], ['female', 'Female'], ['other', 'Other']], { placeholder: 'Select gender' })}
            </div>
            {select('grade', 'Grade/Program', eligibleGrades.map((g) => [g.key, g.label]), {
              placeholder: gradesError ? 'Programs could not be loaded' : grades === null ? 'Loading programs...' : 'Select program'
            })}
            {field('previousSchool', 'Previous school (if applicable)', { required: false })}
          </fieldset>
        )}

        {step === 1 && (
          <fieldset className="form-section">
            <legend><h3 ref={heading} tabIndex={-1}>Parent / guardian information</h3></legend>
            <div className="form-row">
              {field('parentFirstName', 'First name', { autoComplete: 'given-name' })}
              {field('parentLastName', 'Last name', { autoComplete: 'family-name' })}
            </div>
            <div className="form-row">
              {field('email', 'Email', { type: 'email', autoComplete: 'email', hint: 'The school will use this to reach you.' })}
              {field('phone', 'Phone', { type: 'tel', autoComplete: 'tel', inputMode: 'tel' })}
            </div>
            {field('address', 'Address', { autoComplete: 'street-address' })}
            <div className="form-row">
              {field('city', 'City', { autoComplete: 'address-level2' })}
              {field('state', 'State / province', { autoComplete: 'address-level1' })}
              {field('zipCode', 'Zip / postal code', { autoComplete: 'postal-code' })}
            </div>
            <h4>Emergency contact</h4>
            <div className="form-row">
              {field('emergencyContactName', 'Contact name')}
              {field('emergencyContactPhone', 'Contact phone', { type: 'tel', inputMode: 'tel' })}
              {field('emergencyContactRelation', 'Relationship', { placeholder: 'e.g., Grandmother, Uncle' })}
            </div>
          </fieldset>
        )}

        {step === 2 && (
          <fieldset className="form-section">
            <legend><h3 ref={heading} tabIndex={-1}>Additional information</h3></legend>
            <p>All of these are optional.</p>
            {area('medicalConditions', 'Medical conditions or allergies', {
              placeholder: 'Please list any medical conditions, allergies, or medications',
              'aria-describedby': 'hint-medical'
            })}
            <small id="hint-medical">For privacy this is not saved in your browser; it is lost if you leave the page.</small>
            {area('specialNeeds', 'Special needs or accommodations', {
              placeholder: 'Please describe any special needs or accommodations required'
            })}
            {select('howDidYouHear', 'How did you hear about us?',
              [['website', 'Website'], ['social-media', 'Social Media'], ['friend', 'Friend/Referral'],
                ['advertisement', 'Advertisement'], ['other', 'Other']], { required: false, placeholder: 'Select one' })}
            {area('comments', 'Additional comments', { rows: 4, placeholder: "Any additional information you'd like to share" })}
          </fieldset>
        )}

        {step === 3 && (
          <fieldset className="form-section">
            <legend><h3 ref={heading} tabIndex={-1}>Review your application</h3></legend>
            <p>Please check everything below. Use Edit to change a section.</p>
            {review('Student', 0, [
              ['Name', `${data.studentFirstName} ${data.studentLastName}`.trim()],
              ['Date of birth', data.dateOfBirth], ['Gender', gender], ['Grade/Program', gradeLabel(data.grade)],
              ['Previous school', data.previousSchool]
            ])}
            {review('Parent / guardian', 1, [
              ['Name', `${data.parentFirstName} ${data.parentLastName}`.trim()],
              ['Email', data.email], ['Phone', data.phone],
              ['Address', [data.address, data.city, data.state, data.zipCode].filter(Boolean).join(', ')],
              ['Emergency contact', [data.emergencyContactName, data.emergencyContactPhone, data.emergencyContactRelation]
                .filter(Boolean).join(' · ')]
            ])}
            {review('Additional information', 2, [
              ['Medical conditions', data.medicalConditions], ['Special needs', data.specialNeeds],
              ['Heard about us', heard], ['Comments', data.comments]
            ])}

            <div className="consent">
              <label htmlFor="field-consent">
                <input
                  id="field-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  aria-required="true"
                />
                {' '}I have read and accept the{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer">privacy policy</Link>
                {' '}and agree that the school may use this information to process my child&rsquo;s application.
                <span aria-hidden="true"> *</span>
              </label>
            </div>

            <Turnstile onToken={setTurnstileToken} resetSignal={turnstileReset} />
          </fieldset>
        )}

        <div className="form-actions">
          {step > 0 && (
            <button type="button" className="btn btn-secondary" onClick={() => goTo(step - 1)} disabled={submitting}>
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button type="button" className="btn btn-primary" onClick={next}>Next</button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting application…' : 'Submit application'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ApplicationForm;
